<template>
    <div class="plan-cycle-card-group">
        <duration-tab
            v-if="durationListForTab.length > 1"
            :duration-list="durationListForTab"
            :active-duration-index="activeDurationIndex"
            :year-plans-max-discount="yearPlansMaxDiscount"
            align="stretch"
            @change="handleDurationChange"
            class="tab"
        />
        <div
            class="card-grid"
            :class="[
                'cols-' + cardGridCols,
                {
                    'card-grid--single-row': cardGridSingleRow,
                }]"
        >
            <plan-cycle-card
                v-for="(item,index) in currentCards"
                :key="item.plan.id"
                ref="planCards"
                ref-in-for
                :item="item"
                :country-available="countryAvailable"
                :is-current-monthly-cycle="isCurrentMonthlyCycle"
                :promotions="promotions"
                :free-plan-submitting="freePlanSubmitting"
                :subscribe-mode="subscribeMode"
                :curr-sub-plan-id="currSubPlanId"
                :curr-sub-expired="currSubExpired"
                :plan-cards-data-version="planCardsDataVersion"
                @change="$emit('change', $event)"
                @claim-free-plan="$emit('claim-free-plan', $event)"
                @subscribe="$emit('subscribe', $event)"
                :class="{
                    'card-has-recommended-below': checkHasRecommendBelow(index),
                }"
            />
        </div>
    </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import { mapGetters } from 'vuex';
import { EPlanStatus, ESubscribeMode, IDuration, IPlan, IPromotionItem } from '@reolink-cloud/interface';
import DurationTab from '@/components/DurationTab.vue';
import PlanCycleCard from './PlanCycleCard.vue';
import { planDisplayMapper } from './mapper';
import type { IPlanCardItem } from './types';

export default Vue.extend({
    name: 'PlanCycleCardGroup',
    components: {
        DurationTab,
        PlanCycleCard
    },
    props: {
        country: { type: String, required: true },
        countryAvailable: { type: Boolean, default: true },
        category: { type: String as PropType<string>, required: true },
        defaultSelectedPlanId: { type: String, default: '' },
        hasSubscribed: { type: Boolean, default: false },
        subscribeMode: { type: String as PropType<ESubscribeMode>, default: ESubscribeMode.CREATE },
        currSubPlanId: { type: String, default: '' },
        currSubExpired: { type: Boolean, default: false },
        currDeviceGroups: { type: Array as PropType<string[]>, default: () => [] },
        promotions: { type: Array as PropType<IPromotionItem[]>, default: () => [] },
        freePlanSubmitting: { type: Boolean, default: false }
    },
    data() {

        return {
            activeDurationIndex: 0,
            cardList: [] as IPlanCardItem[],
            durationListRaw: [] as IDuration[],
            /** 随 runMapper 递增，子卡片用于重置免费套餐协议勾选 */
            planCardsDataVersion: 0,
            hasMounted: false
        };

    },
    computed: {
        ...mapGetters( ['isMobile'] as const ),
        durationListForTab(): IDuration[] {

            return this.durationListRaw.map( d => ({
                title: this.$tc( 'cycle.' + d.unit, d.amount, { count: d.amount }),
                unit: d.unit,
                amount: d.amount
            }));

        },
        currentCycleKey(): string {

            const d = this.durationListRaw[this.activeDurationIndex];
            return d ? `${d.amount}-${d.unit}` : '';

        },
        currentCards(): IPlanCardItem[] {

            const cards = !this.currentCycleKey
                ? this.cardList
                : this.cardList.filter( item => item.cycleKey === this.currentCycleKey );

            return [...cards].sort(( a, b ) => {

                return Number( this.isFreePlan( b.plan )) - Number( this.isFreePlan( a.plan ));

            });

        },
        isCurrentMonthlyCycle(): boolean {

            return this.currentCycleKey === '1-month';

        },
        cardGridCols(): number {

            if ( this.isMobile ) return 1;
            return this.currentCards.length >= 3 ? 3 : 2;

        },
        /** 桌面端仅一行卡时避免 margin-bottom，防止整行下方多出空白 */
        cardGridSingleRow(): boolean {

            if ( this.isMobile ) return false;
            return this.currentCards.length <= this.cardGridCols;

        },
        yearPlansMaxDiscount(): string | undefined {

            const discounts = this.cardList
                .filter( item => item.plan.duration.unit === 'year' && item.plan.duration.amount === 1 )
                .map( item => {

                    const regularPrice = parseFloat( item.plan.pricing.regularPrice );
                    if ( regularPrice === 0 ) {

                        return 0;

                    }

                    return 100 - 100 * parseFloat( item.plan.pricing.salePrice ) / regularPrice;

                });
            if ( !discounts.length ) {

                return undefined;

            }
            const maxDiscount = Math.max( ...discounts, 0 );
            return maxDiscount >= 1 ? this.$t( 'plan.discount_up_to', {
                discount: maxDiscount.toFixed( 0 )
            }).toString() : undefined;

        }
    },
    created() {

        this.runMapper();

    },
    mounted() {

        this.hasMounted = true;

        this.$nextTick(() => {

            this.emitVisiblePlans();

        });

    },
    watch: {
        country: 'runMapper',
        category: 'runMapper',
        defaultSelectedPlanId: 'runMapper',
        hasSubscribed: 'runMapper',
        subscribeMode: 'runMapper'
    },
    methods: {
        runMapper() {

            const result = planDisplayMapper({
                category: this.category as any,
                country: this.country,
                defaultSelectedPlan: this.subscribeMode === ESubscribeMode.SWITCH ? this.defaultSelectedPlanId : undefined,
                dropPlanIds: [this.currSubPlanId],
                dropFreePlan: this.hasSubscribed,
                onlyKeepPlanCanSwitch: this.hasSubscribed

            });
            this.cardList = result.cardList;
            this.durationListRaw = result.durationList as IDuration[];
            this.activeDurationIndex = 0;
            this.planCardsDataVersion += 1;

            this.emitVisiblePlans();
            this.$emit( 'duration-list-change', this.durationListRaw.map( d => ({ unit: d.unit, amount: d.amount })));

        },
        isFreePlan( plan: IPlan ): boolean {

            return Number( plan.pricing.salePrice ) === 0;

        },
        /** 与 PlanCycleCard 中 disabledByPlanRule / showRecommendStyle 对齐 */
        planDisabledByPlanRule( plan: IPlan ): boolean {

            if ( plan.status === EPlanStatus.RENEW_ONLY ) return true;
            if ( this.subscribeMode === ESubscribeMode.CREATE ) return false;
            if ( this.subscribeMode === ESubscribeMode.SWITCH && Number( plan.pricing.salePrice ) === 0 ) return true;
            return !plan.limitations.switchFromOther;

        },
        itemShowsRecommendStyle( item: IPlanCardItem ): boolean {

            if ( !item.isRecommended || !this.countryAvailable ) return false;
            if ( this.planDisabledByPlanRule( item.plan )) return false;
            return this.currSubExpired || this.subscribeMode === ESubscribeMode.CREATE;

        },
        handleDurationChange( index: number ) {

            this.activeDurationIndex = index;
            this.emitVisiblePlans();

        },
        emitVisiblePlans() {

            const plans = this.currentCards.map( item => item.plan );
            this.$emit( 'visible-plans-change', plans );

        },
        checkHasRecommendBelow( index: number ): boolean {

            if ( this.isMobile ) {

                return false;

            }
            const cols = this.cardGridCols;
            const nextRowStart = ( Math.floor( index / cols ) + 1 ) * cols;
            const nextRowEnd = nextRowStart + cols;
            return this.currentCards.slice( nextRowStart, nextRowEnd ).some( item => this.itemShowsRecommendStyle( item ));

        },
    }
});
</script>

<style lang="less" scoped>
.plan-cycle-card-group {
    .card-grid {
        display: grid;
        /* subgrid：每张卡占 4 行，列间距用 column-gap；行间距用卡 margin-bottom，避免行与行之间出现 24px */
        grid-auto-rows: auto;
        column-gap: 24px;
        row-gap: 0;
        margin-top: 24px;
        /* stretch：每张卡铺满所属四行，subgrid 行高才能一致 */
        align-items: stretch;
        &.cols-2 { grid-template-columns: repeat(2, 1fr); }
        &.cols-3 { grid-template-columns: repeat(3, 1fr); }
        &.cols-1 {
            grid-template-columns: 1fr;
            box-sizing: border-box;
        }
        /* 垂直方向卡与卡间距（多行堆叠时；单行多列见 .card-grid--single-row） */
        /deep/ .plan-cycle-card {
            margin-bottom: 24px;
        }
        /* 有推荐角标的列表：卡行之间在 24px 基础上 +10（仍作用在整卡上，不破坏 subgrid） */
        &.card-grid--has-recommended /deep/ .plan-cycle-card {
            margin-bottom: 34px;
        }
        /deep/ .plan-cycle-card:last-child {
            .mobile({
                margin-bottom: 0;
            });
        }
        &.card-grid--single-row /deep/ .plan-cycle-card {
            margin-bottom: 0;
        }
        .card-has-recommended-below {
            margin-bottom: 44px;
        }
    }
    .tab {
        width: 100%;
        margin: 52px 0;
        max-width: 100%;
        /deep/ .tabItem {
            flex: 1;
        }
        .mobile({
            margin-top: 24px;
            margin-bottom: 12px;
        });
    }
}
</style>
